import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { servicesApi, uploadApi } from '../lib/api';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, Plus, X, Edit } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const ServiceForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id) && location.pathname.includes('/edit');
  const isView = Boolean(id) && !location.pathname.includes('/edit');

  // Type definitions matching backend structure
type Product = {
  name: string;
  description: string;
};

interface TransformationStep {
  step: string;
  description: string;
  icon: File | string | null;
}

interface FeatureItem {
  title: string;
  description: string;
  icon: File | string | null;
}

type Branch = {
  name: string;
  description: string;
};

type ServiceItem = {
  name: string;
  description: string;
  icon: File | string | null;
};

type AiCard = {
  title: string;
  description: string;
};

 const [formData, setFormData] = useState({
  title_en: '',
  title_ar: '',
  subtitle_en: '',
  subtitle_ar: '',
  description_part1_en: '',
  description_part1_ar: '',
  description_part2_en: '',
  description_part2_ar: '',
  products_title_en: '',
  products_title_ar: '',
  products_en: [] as Product[],
  products_ar: [] as Product[],
  icon_en: '',
  icon_ar: '',
  order: 0,

  // ✅ Digital Transformation
  transformation_title_en: '',
  transformation_title_ar: '',
  transformation_icon: null as string | null,
  transformation_steps_en: [] as TransformationStep[],
  transformation_steps_ar: [] as TransformationStep[],


  // ✅ Features
  feature_title_en: '',
feature_title_ar: '',

  // features_en: [] as { title: string; icon: File | string | null }[],
  // features_ar: [] as { title: string; icon: File | string | null }[],

  feature_icon: null as string | null,
  feature_en: [] as FeatureItem[],
  feature_ar: [] as FeatureItem[],
  // ✅  Branches
  branches_title_en: '',
  branches_title_ar: '',
  branches_icon: null as string | null,
  branches_en: [] as Branch[],
  branches_ar: [] as Branch[],

  // ✅ Solid & Flexible Services
  services_title_en: '',
  services_title_ar: '',
  services_icon: null as string | null,
  services_en: [] as ServiceItem[],
  services_ar: [] as ServiceItem[],
  // ✅ AI Chat Solutions
  ai_title_en: '',
  ai_title_ar: '',
  ai_icon: null as string | null,
  ai_cards_en: [] as AiCard[],
  ai_cards_ar: [] as AiCard[],
  ai_paragraph_en: "",
  ai_paragraph_ar: "",
//   // Units (if you still need them)
// units_title_en: "",
// units_title_ar: "",
// units_icon: null as File | null,
//   units_en: [] as Unit[],
//   units_ar: [] as Unit[],


});
const prepareFormData = (data: typeof formData) => {
  const prepared = { ...data };
  const originalFormData = data; // Keep reference to original for double-check

  // Convert null icon values to empty strings so they can be sent to clear icons
  const iconFields = ['icon_en', 'icon_ar', 'transformation_icon', 'branches_icon', 'services_icon', 'ai_icon', 'feature_icon'];
  iconFields.forEach(iconKey => {
    const currentValue = prepared[iconKey as keyof typeof prepared];
    if (currentValue === null || currentValue === undefined) {
      (prepared as any)[iconKey] = '';
      console.log(`[CONVERTED] ${iconKey} from null/undefined to empty string`);
    }
  });
  
  // Ensure ALL title fields are strings (not null/undefined) - same as feature_title
  // This allows all fields to be cleared when empty
  const allTitleFields = [
    'feature_title_en', 'feature_title_ar',
    'services_title_en', 'services_title_ar',
    'ai_title_en', 'ai_title_ar',
    'transformation_title_en', 'transformation_title_ar',
    'branches_title_en', 'branches_title_ar',
    'products_title_en', 'products_title_ar',
    'title_en', 'title_ar',
    'subtitle_en', 'subtitle_ar',
    'description_part1_en', 'description_part1_ar',
    'description_part2_en', 'description_part2_ar',
    'ai_paragraph_en', 'ai_paragraph_ar'
  ];
  allTitleFields.forEach(fieldKey => {
    const currentValue = prepared[fieldKey as keyof typeof prepared];
    if (currentValue === null || currentValue === undefined) {
      (prepared as any)[fieldKey] = '';
      console.log(`[CONVERTED] ${fieldKey} from null/undefined to empty string`);
    }
  });

  // Filter empty items from arrays
  prepared.products_en = prepared.products_en.filter((item: Product) => item && item.name && item.name.trim() !== '');
  prepared.products_ar = prepared.products_ar.filter((item: Product) => item && item.name && item.name.trim() !== '');
  prepared.feature_en = prepared.feature_en.filter((item: FeatureItem) => item && item.title && item.title.trim() !== '');
  prepared.feature_ar = prepared.feature_ar.filter((item: FeatureItem) => item && item.title && item.title.trim() !== '');
  prepared.ai_cards_en = prepared.ai_cards_en.filter((card: AiCard) => card && (card.title?.trim() || card.description?.trim()));
  prepared.ai_cards_ar = prepared.ai_cards_ar.filter((card: AiCard) => card && (card.title?.trim() || card.description?.trim()));
  prepared.services_en = prepared.services_en.filter((item: ServiceItem) => item && item.name && item.name.trim() !== '');
  prepared.services_ar = prepared.services_ar.filter((item: ServiceItem) => item && item.name && item.name.trim() !== '');
  prepared.transformation_steps_en = prepared.transformation_steps_en.filter((item: TransformationStep) => item && item.step && item.step.trim() !== '');
  prepared.transformation_steps_ar = prepared.transformation_steps_ar.filter((item: TransformationStep) => item && item.step && item.step.trim() !== '');
  prepared.branches_en = prepared.branches_en.filter((item: Branch) => item && item.name && item.name.trim() !== '');
  prepared.branches_ar = prepared.branches_ar.filter((item: Branch) => item && item.name && item.name.trim() !== '');

  // Keep all fields (including empty ones) so backend can update them
  // Only remove null values, but keep empty strings for clearing fields
  (Object.keys(prepared) as (keyof typeof prepared)[]).forEach((key) => {
    const value = prepared[key];
    // Don't delete array fields - let backend handle empty arrays
    const isArrayField = [
      'products_en', 'products_ar',
      'transformation_steps_en', 'transformation_steps_ar',
      'feature_en', 'feature_ar',
      'branches_en', 'branches_ar',
      'services_en', 'services_ar',
      'ai_cards_en', 'ai_cards_ar'
    ].includes(key as string);
    
    // Only remove null values, keep empty strings so fields can be cleared
    // Keep array fields and empty strings for updates
    if (!isArrayField && value === null) {
      delete prepared[key];
    }
    // Empty strings are kept so backend can clear the fields
  });

  // تحويل كل شيء إلى FormData للإرسال
  const form = new FormData();

  // Array fields that must always be sent (even if empty)
  const arrayFields = [
    'feature_en', 'feature_ar',
    'branches_en', 'branches_ar',
    'ai_cards_en', 'ai_cards_ar',
    'products_en', 'products_ar',
    'transformation_steps_en', 'transformation_steps_ar',
    'services_en', 'services_ar'
  ];

  // List of all text/string fields that should always be sent (even if empty)
  const textFields = [
    'title_en', 'title_ar',
    'subtitle_en', 'subtitle_ar',
    'description_part1_en', 'description_part1_ar',
    'description_part2_en', 'description_part2_ar',
    'products_title_en', 'products_title_ar',
    'icon_en', 'icon_ar',
    'transformation_title_en', 'transformation_title_ar',
    'feature_title_en', 'feature_title_ar',
    'branches_title_en', 'branches_title_ar',
    'services_title_en', 'services_title_ar',
    'ai_title_en', 'ai_title_ar',
    'ai_paragraph_en', 'ai_paragraph_ar',
    'transformation_icon', 'branches_icon', 'services_icon', 'ai_icon', 'feature_icon'
  ];

  // Send all text fields including empty strings
  // CRITICAL: Always send these fields, even if empty, so backend can update them
  textFields.forEach(fieldKey => {
    // Get value from prepared data (should already be converted from null to '')
    // If field doesn't exist in prepared, it will be undefined
    let value = prepared[fieldKey as keyof typeof prepared];
    
    // If field doesn't exist at all, get it from original formData
    if (value === undefined) {
      value = originalFormData[fieldKey as keyof typeof originalFormData];
    }
    
    // Final safety check: if still null/undefined, convert to empty string
    if (value === null || value === undefined) {
      value = '';
    }
    
    // Always send as string (empty string if null, undefined, or already empty)
    const stringValue = String(value || '');
    
    // Explicitly append even if empty - FormData will send it as empty string
    form.append(fieldKey, stringValue);
    
    // Debug log for feature fields
    if (fieldKey.includes('feature_title') || fieldKey === 'feature_icon') {
      console.log(`[SENDING] ${fieldKey}:`, `"${stringValue}"`, `(type: ${typeof stringValue}, length: ${stringValue.length}, isEmpty: ${stringValue === ''})`);
    }
  });
  
  // Send order field
  if (typeof prepared.order === 'number') {
    form.append('order', prepared.order.toString());
  }

  // الحقول العادية - send any other fields that might exist
  (Object.keys(prepared) as (keyof typeof prepared)[]).forEach((key) => {
    const value = prepared[key];
    // Skip array fields and fields we've already handled
    if (!arrayFields.includes(key as string) && !textFields.includes(key as string) && key !== 'order') {
      // Send strings (including empty strings) and numbers
      if (typeof value === 'string') {
        form.append(key, value);
      } else if (typeof value === 'number') {
        form.append(key, value.toString());
      }
    }
  });

  // Handle arrays - send in Laravel native format for FormData
  // Products array - new structure only requires name, description is optional
  ['products_en', 'products_ar'].forEach(arrKey => {
    const array = prepared[arrKey as keyof typeof prepared] as Product[];
    if (Array.isArray(array)) {
      if (array.length > 0) {
        array.forEach((item: Product, index: number) => {
          if (item && item.name) {
            form.append(`${arrKey}[${index}][name]`, item.name);
            // Only send description if it exists and is not empty
            if (item.description && item.description.trim()) {
              form.append(`${arrKey}[${index}][description]`, item.description);
            }
          }
        });
      } else {
        // Send empty array as JSON to clear existing data
        form.append(arrKey, '[]');
      }
    }
  });

  // Transformation steps array
  ['transformation_steps_en', 'transformation_steps_ar'].forEach(arrKey => {
    const array = prepared[arrKey as keyof typeof prepared] as TransformationStep[];
    if (Array.isArray(array)) {
      if (array.length > 0) {
        array.forEach((item: TransformationStep, index: number) => {
          if (item && item.step) {
            form.append(`${arrKey}[${index}][step]`, item.step);
            if (item.description) {
              form.append(`${arrKey}[${index}][description]`, item.description);
            }
            if (item.icon) {
              if (item.icon instanceof File) {
                form.append(`${arrKey}[${index}][icon]`, item.icon);
              } else if (typeof item.icon === 'string') {
                form.append(`${arrKey}[${index}][icon]`, item.icon);
              }
            }
          }
        });
      } else {
        // Send empty array as JSON to clear existing data
        form.append(arrKey, '[]');
      }
    }
  });

  // Features array - always send, even if empty
  ['feature_en', 'feature_ar'].forEach(arrKey => {
    const array = prepared[arrKey as keyof typeof prepared] as FeatureItem[];
    if (Array.isArray(array)) {
      if (array.length > 0) {
        array.forEach((item: FeatureItem, index: number) => {
          if (item && item.title) {
            form.append(`${arrKey}[${index}][title]`, item.title);
            if (item.description) {
              form.append(`${arrKey}[${index}][description]`, item.description);
            }
            if (item.icon) {
              if (item.icon instanceof File) {
                form.append(`${arrKey}[${index}][icon]`, item.icon);
              } else if (typeof item.icon === 'string') {
                form.append(`${arrKey}[${index}][icon]`, item.icon);
              }
            }
          }
        });
      } else {
        // For empty arrays, send as empty JSON string
        // Backend should parse this or handle missing fields as empty arrays
        form.append(arrKey, '[]');
      }
    }
  });

  // Branches array - always send, even if empty
  ['branches_en', 'branches_ar'].forEach(arrKey => {
    const array = prepared[arrKey as keyof typeof prepared] as Branch[];
    if (Array.isArray(array)) {
      if (array.length > 0) {
        array.forEach((item: Branch, index: number) => {
          if (item && item.name) {
            form.append(`${arrKey}[${index}][name]`, item.name);
            if (item.description) {
              form.append(`${arrKey}[${index}][description]`, item.description);
            }
          }
        });
      } else {
        // For empty arrays, send as empty JSON string
        form.append(arrKey, '[]');
      }
    }
  });

  // Services array
  ['services_en', 'services_ar'].forEach(arrKey => {
    const array = prepared[arrKey as keyof typeof prepared] as ServiceItem[];
    if (Array.isArray(array)) {
      if (array.length > 0) {
        array.forEach((item: ServiceItem, index: number) => {
          if (item && item.name) {
            form.append(`${arrKey}[${index}][name]`, item.name);
            if (item.description) {
              form.append(`${arrKey}[${index}][description]`, item.description);
            }
            if (item.icon) {
              if (item.icon instanceof File) {
                form.append(`${arrKey}[${index}][icon]`, item.icon);
              } else if (typeof item.icon === 'string') {
                form.append(`${arrKey}[${index}][icon]`, item.icon);
              }
            }
          }
        });
      } else {
        // Send empty array as JSON to clear existing data
        form.append(arrKey, '[]');
      }
    }
  });

  // AI Cards array - always send, even if empty
  ['ai_cards_en', 'ai_cards_ar'].forEach(arrKey => {
    const array = prepared[arrKey as keyof typeof prepared] as AiCard[];
    if (Array.isArray(array)) {
      if (array.length > 0) {
        array.forEach((item: AiCard, index: number) => {
          if (item && item.title) {
            form.append(`${arrKey}[${index}][title]`, item.title);
            if (item.description) {
              form.append(`${arrKey}[${index}][description]`, item.description);
            }
          }
        });
      } else {
        // For empty arrays, send as empty JSON string
        form.append(arrKey, '[]');
      }
    }
  });

  // CRITICAL: Force append ALL string fields as the VERY LAST step
  // This ensures they're definitely in FormData even if something went wrong above
  // We do this at the end so it's guaranteed to be in the final FormData
  // IMPORTANT: Some multipart parsers skip empty strings, so we need to ensure
  // ALL string fields are always present, even if empty, so backend can clear them
  
  // All string fields that should always be sent (even if empty)
  const allStringFields = [
    'title_en', 'title_ar',
    'subtitle_en', 'subtitle_ar',
    'description_part1_en', 'description_part1_ar',
    'description_part2_en', 'description_part2_ar',
    'products_title_en', 'products_title_ar',
    'icon_en', 'icon_ar',
    'transformation_title_en', 'transformation_title_ar',
    'transformation_icon',
    'feature_title_en', 'feature_title_ar',
    'feature_icon',
    'branches_title_en', 'branches_title_ar',
    'branches_icon',
    'services_title_en', 'services_title_ar',
    'services_icon',
    'ai_title_en', 'ai_title_ar',
    'ai_icon',
    'ai_paragraph_en', 'ai_paragraph_ar'
  ];
  
  // First, remove any existing entries for these fields by creating a new FormData
  // (FormData doesn't support delete, so we rebuild it)
  const finalForm = new FormData();
  const existingEntries = Array.from(form.entries());
  
  // Copy all existing entries except the string fields (we'll add them fresh)
  existingEntries.forEach(([key, value]) => {
    if (!allStringFields.includes(key)) {
      // Handle both string and File values
      // FormDataEntryValue is string | File
      if (value instanceof File) {
        finalForm.append(key, value);
      } else {
        finalForm.append(key, String(value));
      }
    }
  });
  
  // Now append ALL string fields with their values
  // For empty/null values, send "__NULL__" so backend can convert to null
  allStringFields.forEach(fieldKey => {
    // Get the value from original formData (not prepared, to get the real current value)
    const originalValue = originalFormData[fieldKey as keyof typeof originalFormData];
    
    // Convert to string: if null/undefined/empty, send "__NULL__" for backend to convert to null
    // Otherwise send the actual string value
    let finalValue: string;
    if (originalValue === null || originalValue === undefined || originalValue === '') {
      finalValue = '__NULL__'; // Special marker for backend to convert to null
    } else {
      finalValue = String(originalValue);
    }
    
    // Always append - backend will convert "__NULL__" to null
    finalForm.append(fieldKey, finalValue);
    
    // Log ALL fields for debugging (especially empty/null ones)
    const isEmpty = originalValue === null || originalValue === undefined || originalValue === '';
    const isImportant = fieldKey.includes('title') || fieldKey.includes('icon') || fieldKey.includes('paragraph');
    const isAIField = fieldKey.startsWith('ai_');
    if (isEmpty || isImportant || isAIField) {
      console.log(`[FINAL APPEND] ${fieldKey}:`, `"${finalValue}"`, `(original: ${originalValue}, type: ${typeof originalValue}, isEmpty: ${isEmpty}, sendingAs: ${finalValue})`);
    }
  });
  
  // Final verification: Log all FormData entries for string fields
  const finalEntries = Array.from(finalForm.entries());
  console.log('=== Final FormData Verification (before return) ===');
  const importantFields = ['feature_title_en', 'feature_title_ar', 'feature_icon', 
                          'services_title_en', 'services_title_ar', 'services_icon',
                          'ai_title_en', 'ai_title_ar', 'ai_icon'];
  importantFields.forEach(fieldKey => {
    const entries = finalEntries.filter(([key]) => key === fieldKey);
    if (entries.length > 0) {
      const values = entries.map(([k, v]) => `"${v}"`).join(', ');
      console.log(`✓ ${fieldKey} is in FormData:`, values, `(count: ${entries.length})`);
    } else {
      console.error(`✗ ${fieldKey} is MISSING from FormData!`);
    }
  });
  
  return finalForm;
};

  // Product inputs
  const [newProductEn, setNewProductEn] = useState({ name: '', description: '' });
  const [newProductAr, setNewProductAr] = useState({ name: '', description: '' });
  
  // Branch inputs
  const [newBranchEn, setNewBranchEn] = useState({ name: '', description: '' });
  const [newBranchAr, setNewBranchAr] = useState({ name: '', description: '' });

  // AI Card inputs
  const [newAiEn, setNewAiEn] = useState<AiCard>({ title: "", description: "" });
  const [newAiAr, setNewAiAr] = useState<AiCard>({ title: "", description: "" });

  // Transformation Steps inputs
  const [newStepEn, setNewStepEn] = useState({ step: '', description: '', icon: null as string | null });
  const [newStepAr, setNewStepAr] = useState({ step: '', description: '', icon: null as string | null });

  // Service inputs
  const [newServiceEn, setNewServiceEn] = useState({ name: '', description: '', icon: null as string | null });
  const [newServiceAr, setNewServiceAr] = useState({ name: '', description: '', icon: null as string | null });
  
  // Feature inputs
  const [newFeatureEn, setNewFeatureEn] = useState({ title: '', description: '', icon: null as string | null });
  const [newFeatureAr, setNewFeatureAr] = useState({ title: '', description: '', icon: null as string | null });
// Add feature
const addFeature = (lang: 'en' | 'ar') => {
  const newFeature = lang === 'en' ? newFeatureEn : newFeatureAr;
  if (newFeature.title.trim()) {
    setFormData(prev => ({
      ...prev,
      [`feature_${lang}`]: [
        ...prev[`feature_${lang}` as keyof typeof prev] as FeatureItem[],
        { title: newFeature.title, description: newFeature.description || '', icon: newFeature.icon }
      ],
    }));
    if (lang === 'en') {
      setNewFeatureEn({ title: '', description: '', icon: null });
    } else {
      setNewFeatureAr({ title: '', description: '', icon: null });
    }
  }
};

// Remove feature
const removeFeature = (lang: 'en' | 'ar', index: number) => {
  setFormData(prev => ({
    ...prev,
    [`feature_${lang}`]: (prev[`feature_${lang}` as keyof typeof prev] as FeatureItem[]).filter((_, i) => i !== index),
  }));
};


const addItem = (lang: 'en' | 'ar') => {
  const newService = lang === 'en' ? newServiceEn : newServiceAr;
  if (newService.name.trim()) {
    setFormData(prev => ({
      ...prev,
      [`services_${lang}`]: [
        ...prev[`services_${lang}` as keyof typeof prev] as ServiceItem[],
        { name: newService.name, description: newService.description || '', icon: newService.icon }
      ]
    }));
    if (lang === 'en') {
      setNewServiceEn({ name: '', description: '', icon: null });
    } else {
      setNewServiceAr({ name: '', description: '', icon: null });
    }
  }
};


const removeItem = (lang: 'en' | 'ar', index: number) => {
  setFormData(prev => ({
    ...prev,
    [`services_${lang}`]: (prev[`services_${lang}` as keyof typeof prev] as ServiceItem[]).filter((_, i) => i !== index)
  }));
};




const addStep = (lang: 'en' | 'ar') => {
  const newStep = lang === 'en' ? newStepEn : newStepAr;
  if (newStep.step.trim()) {
    setFormData(prev => ({
      ...prev,
      [`transformation_steps_${lang}`]: [
        ...prev[`transformation_steps_${lang}` as keyof typeof prev] as TransformationStep[],
        { step: newStep.step, description: newStep.description || '', icon: newStep.icon }
      ]
    }));
    if (lang === 'en') {
      setNewStepEn({ step: '', description: '', icon: null });
    } else {
      setNewStepAr({ step: '', description: '', icon: null });
    }
  }
};

const removeStep = (lang: 'en' | 'ar', index: number) => {
  setFormData(prev => ({
    ...prev,
    [`transformation_steps_${lang}`]: (prev[`transformation_steps_${lang}` as keyof typeof prev] as TransformationStep[]).filter((_, i) => i !== index)
  }));
};



const addBranch = (lang: 'en' | 'ar') => {
  const newBranch = lang === 'en' ? newBranchEn : newBranchAr;
  if (newBranch.name.trim()) {
    setFormData(prev => ({
      ...prev,
      [`branches_${lang}`]: [
        ...prev[`branches_${lang}` as keyof typeof prev] as Branch[],
        { name: newBranch.name, description: newBranch.description || '' }
      ]
    }));
    if (lang === 'en') {
      setNewBranchEn({ name: '', description: '' });
    } else {
      setNewBranchAr({ name: '', description: '' });
    }
  }
};

const removeBranch = (lang: 'en' | 'ar', index: number) => {
  setFormData(prev => ({
    ...prev,
    [`branches_${lang}`]: (prev[`branches_${lang}` as keyof typeof prev] as Branch[]).filter((_, i) => i !== index)
  }));
};



// const addUnit = (lang: 'en' | 'ar') => {
//   if (lang === 'en' && newUnitEn.trim()) {
//     setFormData({
//       ...formData,
//       units_en: [...formData.units_en, { title: newUnitEn, icon: null }]
//     });
//     setNewUnitEn('');
//   }
//   if (lang === 'ar' && newUnitAr.trim()) {
//     setFormData({
//       ...formData,
//       units_ar: [...formData.units_ar, { title: newUnitAr, icon: null }]
//     });
//     setNewUnitAr('');
//   }
// };

// const removeUnit = (lang: 'en' | 'ar', index: number) => {
//   if (lang === 'en') {
//     setFormData({
//       ...formData,
//       units_en: formData.units_en.filter((_, i) => i !== index)
//     });
//   } else {
//     setFormData({
//       ...formData,
//       units_ar: formData.units_ar.filter((_, i) => i !== index)
//     });
//   }
// };

// const handleUnitIconUpload = (e: React.ChangeEvent<HTMLInputElement>, lang: 'en' | 'ar', index: number) => {
//   const file = e.target.files?.[0];
//   if (!file) return;

//   if (lang === 'en') {
//     const updated = [...formData.units_en];
//     updated[index].icon = file;
//     setFormData({ ...formData, units_en: updated });
//   } else {
//     const updated = [...formData.units_ar];
//     updated[index].icon = file;
//     setFormData({ ...formData, units_ar: updated });
//   }
// };

// Add AI Card
const addAi = (lang: "en" | "ar") => {
  const newAi = lang === "en" ? newAiEn : newAiAr;
  if (newAi.title.trim() || newAi.description.trim()) {
    setFormData((prev) => ({
      ...prev,
      [`ai_cards_${lang}`]: [
        ...prev[`ai_cards_${lang}` as keyof typeof prev] as AiCard[],
        { title: newAi.title, description: newAi.description || '' }
      ],
    }));
    if (lang === "en") {
      setNewAiEn({ title: "", description: "" });
    } else {
      setNewAiAr({ title: "", description: "" });
    }
  }
};
const removeAi = (lang: "en" | "ar", index: number) => {
  setFormData((prev) => ({
    ...prev,
    [`ai_cards_${lang}`]: (prev[`ai_cards_${lang}` as keyof typeof prev] as AiCard[]).filter((_, i) => i !== index),
  }));
};

const handleAiChange = (
  lang: "en" | "ar",
  index: number,
  field: "title" | "description",
  value: string
) => {
  setFormData((prev) => {
    const updated = [...(prev[`ai_cards_${lang}` as keyof typeof prev] as AiCard[])];
    updated[index][field] = value;

    return {
      ...prev,
      [`ai_cards_${lang}`]: updated,
    };
  });
};



  const { data: service, isLoading } = useQuery({
    queryKey: ['services', id, 'raw'],
    queryFn: async () => {
      const response = await servicesApi.getById(id!, true);
      // Handle new response structure: { status: "success", data: {...} }
      // or fallback to old structure: {...}
      if (response.data?.status === 'success' && response.data?.data) {
        return response.data.data;
      }
      // Fallback for old structure or direct data
      return response.data?.data || response.data;
    },
    enabled: isEdit || isView,
  });

  useEffect(() => {
    if (service && (isEdit || isView)) {
      console.log('Loading service data:', service); // Debug log
      console.log('AI fields in service:', {
        ai_title: service.ai_title,
        ai_title_en: service.ai_title_en,
        ai_title_ar: service.ai_title_ar,
        ai_paragraph: service.ai_paragraph,
        ai_paragraph_en: service.ai_paragraph_en,
        ai_paragraph_ar: service.ai_paragraph_ar,
        ai_cards: service.ai_cards,
        ai_cards_en: service.ai_cards_en,
        ai_cards_ar: service.ai_cards_ar
      }); // Debug AI fields
      setFormData({
        title_en: service.title_en || '',
        title_ar: service.title_ar || '',
        subtitle_en: service.subtitle_en || '',
        subtitle_ar: service.subtitle_ar || '',
        description_part1_en: service.description_part1_en || '',
        description_part1_ar: service.description_part1_ar || '',
        description_part2_en: service.description_part2_en || '',
        description_part2_ar: service.description_part2_ar || '',
        products_title_en: service.products_title_en || '',
        products_title_ar: service.products_title_ar || '',
        products_en: service.products_en ? (() => {
          const products = typeof service.products_en === 'string' ? JSON.parse(service.products_en) : service.products_en;
          return Array.isArray(products) ? products.map((p: any) => {
            if (typeof p === 'string') {
              return { name: p, description: '' };
            }
            // Handle new structure: { name: string } or { name: string, description: string }
            return { name: p.name || '', description: p.description || '' };
          }) : [];
        })() : [],
        products_ar: service.products_ar ? (() => {
          const products = typeof service.products_ar === 'string' ? JSON.parse(service.products_ar) : service.products_ar;
          return Array.isArray(products) ? products.map((p: any) => {
            if (typeof p === 'string') {
              return { name: p, description: '' };
            }
            // Handle new structure: { name: string } or { name: string, description: string }
            return { name: p.name || '', description: p.description || '' };
          }) : [];
        })() : [],
        icon_en: service.icon_en || '',
        icon_ar: service.icon_ar || '',
        order: service.order || 0,

        transformation_title_en: service.transformation_title_en || '',
transformation_title_ar: service.transformation_title_ar || '',
transformation_icon: service.transformation_icon ? (typeof service.transformation_icon === 'string' ? service.transformation_icon : null) : null,
        transformation_steps_en: service.transformation_steps_en ? (() => {
          const steps = typeof service.transformation_steps_en === 'string' ? JSON.parse(service.transformation_steps_en) : service.transformation_steps_en;
          return Array.isArray(steps) ? steps.map((step: any) => {
            if (typeof step === 'string') {
              return { step: step, description: '', icon: null };
            }
            // Handle new structure: { step: string, icon?: string } or { step: string, description?: string, icon?: string }
            return { 
              step: step.step || step.title || '', 
              description: step.description || '', 
              icon: step.icon || null 
            };
          }) : [];
        })() : [],
        transformation_steps_ar: service.transformation_steps_ar ? (() => {
          const steps = typeof service.transformation_steps_ar === 'string' ? JSON.parse(service.transformation_steps_ar) : service.transformation_steps_ar;
          return Array.isArray(steps) ? steps.map((step: any) => {
            if (typeof step === 'string') {
              return { step: step, description: '', icon: null };
            }
            // Handle new structure: { step: string, icon?: string } or { step: string, description?: string, icon?: string }
            return { 
              step: step.step || step.title || '', 
              description: step.description || '', 
              icon: step.icon || null 
            };
          }) : [];
        })() : [],

feature_title_en: service.feature_title_en || '',
feature_title_ar: service.feature_title_ar || '',
feature_icon: service.feature_icon ? (typeof service.feature_icon === 'string' ? service.feature_icon : null) : null,
feature_en: service.feature_en ? (Array.isArray(service.feature_en) ? service.feature_en.map((f: any) => ({
          title: f.title || '',
          description: f.description || '',
          icon: f.icon || null
        })) : (typeof service.feature_en === 'string' ? JSON.parse(service.feature_en).map((f: any) => ({
          title: f.title || '',
          description: f.description || '',
          icon: f.icon || null
        })) : [])) : [],
feature_ar: service.feature_ar ? (Array.isArray(service.feature_ar) ? service.feature_ar.map((f: any) => ({
          title: f.title || '',
          description: f.description || '',
          icon: f.icon || null
        })) : (typeof service.feature_ar === 'string' ? JSON.parse(service.feature_ar).map((f: any) => ({
          title: f.title || '',
          description: f.description || '',
          icon: f.icon || null
        })) : [])) : [],

// Branches
branches_title_en: service.branches_title_en || '',
branches_title_ar: service.branches_title_ar || '',
branches_icon: service.branches_icon ? (typeof service.branches_icon === 'string' ? service.branches_icon : null) : null,
branches_en: service.branches_en ? (() => {
          const branches = typeof service.branches_en === 'string' ? JSON.parse(service.branches_en) : service.branches_en;
          return Array.isArray(branches) ? branches.map((b: any) => 
            typeof b === 'string' ? { name: b, description: '' } : 
            { name: b.name || b.title || '', description: b.description || '' }
          ) : [];
        })() : [],
branches_ar: service.branches_ar ? (() => {
          const branches = typeof service.branches_ar === 'string' ? JSON.parse(service.branches_ar) : service.branches_ar;
          return Array.isArray(branches) ? branches.map((b: any) => 
            typeof b === 'string' ? { name: b, description: '' } : 
            { name: b.name || b.title || '', description: b.description || '' }
          ) : [];
        })() : [],

// Solid & Flexible Services
services_title_en: service.services_title_en || '',
services_title_ar: service.services_title_ar || '',
services_icon: service.services_icon ? (typeof service.services_icon === 'string' ? service.services_icon : null) : null,
        services_en: service.services_en ? (() => {
          const services = typeof service.services_en === 'string' ? JSON.parse(service.services_en) : service.services_en;
          return Array.isArray(services) ? services.map((s: any) => {
            if (typeof s === 'string') {
              return { name: s, description: '', icon: null };
            }
            // Handle new structure: { name: string, description?: string, icon?: string }
            return { 
              name: s.name || s.title || '', 
              description: s.description || '', 
              icon: s.icon || null 
            };
          }) : [];
        })() : [],
        services_ar: service.services_ar ? (() => {
          const services = typeof service.services_ar === 'string' ? JSON.parse(service.services_ar) : service.services_ar;
          return Array.isArray(services) ? services.map((s: any) => {
            if (typeof s === 'string') {
              return { name: s, description: '', icon: null };
            }
            // Handle new structure: { name: string, description?: string, icon?: string }
            return { 
              name: s.name || s.title || '', 
              description: s.description || '', 
              icon: s.icon || null 
            };
          }) : [];
        })() : [],

 // ✅ AI Chat
      // When raw=true, we get ai_title_en and ai_title_ar directly
      // If somehow we get ai_title (from ServiceResource), we can't split it properly
      // So we prioritize the raw format fields
      ai_title_en: service.ai_title_en || '',
      ai_title_ar: service.ai_title_ar || '',
      ai_icon: service.ai_icon ? (typeof service.ai_icon === 'string' ? service.ai_icon : null) : null,
      ai_cards_en: service.ai_cards_en ? (() => {
          const cards = typeof service.ai_cards_en === 'string' ? JSON.parse(service.ai_cards_en) : service.ai_cards_en;
          return Array.isArray(cards) ? cards.map((card: any) => ({ 
            title: card.title || '', 
            description: card.description || card.paragraph || '' 
          })) : [];
        })() : (service.ai_cards ? (() => {
          // Handle resource format where ai_cards might be a single localized array
          const cards = typeof service.ai_cards === 'string' ? JSON.parse(service.ai_cards) : service.ai_cards;
          return Array.isArray(cards) ? cards.map((card: any) => ({ 
            title: card.title || '', 
            description: card.description || card.paragraph || '' 
          })) : [];
        })() : []),
      ai_cards_ar: service.ai_cards_ar ? (() => {
          const cards = typeof service.ai_cards_ar === 'string' ? JSON.parse(service.ai_cards_ar) : service.ai_cards_ar;
          return Array.isArray(cards) ? cards.map((card: any) => ({ 
            title: card.title || '', 
            description: card.description || card.paragraph || '' 
          })) : [];
        })() : (service.ai_cards ? (() => {
          // Handle resource format where ai_cards might be a single localized array
          const cards = typeof service.ai_cards === 'string' ? JSON.parse(service.ai_cards) : service.ai_cards;
          return Array.isArray(cards) ? cards.map((card: any) => ({ 
            title: card.title || '', 
            description: card.description || card.paragraph || '' 
          })) : [];
        })() : []),
      // When raw=true, we get ai_paragraph_en and ai_paragraph_ar directly
      ai_paragraph_en: service.ai_paragraph_en || '',
      ai_paragraph_ar: service.ai_paragraph_ar || '',


// units_title_en: service.units_title_en || "",
// units_title_ar: service.units_title_ar || "",
// units_icon: service.units_icon || "",
// units_en: service.units_en || [],
// units_ar: service.units_ar || [],

      });
    }
  }, [service, isEdit, isView]);

  const handleIconUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: keyof typeof formData
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        toast.loading(i18n.language === 'ar' ? 'جاري رفع الأيقونة...' : 'Uploading icon...');
        const response = await uploadApi.uploadImage(file);
        if (response.data.success) {
          setFormData((prev) => ({ ...prev, [fieldName]: response.data.url }));
          toast.dismiss();
          toast.success(i18n.language === 'ar' ? 'تم رفع الأيقونة بنجاح' : 'Icon uploaded successfully');
        }
      } catch (error: any) {
        toast.dismiss();
        toast.error(error?.response?.data?.message || (i18n.language === 'ar' ? 'فشل رفع الأيقونة' : 'Failed to upload icon'));
      }
    }
  };

  const handleItemIconUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    arrayKey: 'transformation_steps_en' | 'transformation_steps_ar' | 'services_en' | 'services_ar' | 'feature_en' | 'feature_ar',
    index: number
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        toast.loading(i18n.language === 'ar' ? 'جاري رفع الأيقونة...' : 'Uploading icon...');
        const response = await uploadApi.uploadImage(file);
        if (response.data.success) {
          setFormData((prev) => {
            const array = [...(prev[arrayKey] as any[])];
            array[index] = { ...array[index], icon: response.data.url };
            return { ...prev, [arrayKey]: array };
          });
          toast.dismiss();
          toast.success(i18n.language === 'ar' ? 'تم رفع الأيقونة بنجاح' : 'Icon uploaded successfully');
        }
      } catch (error: any) {
        toast.dismiss();
        toast.error(error?.response?.data?.message || (i18n.language === 'ar' ? 'فشل رفع الأيقونة' : 'Failed to upload icon'));
      }
    }
  };

  const handleItemIconRemove = (
    arrayKey: 'transformation_steps_en' | 'transformation_steps_ar' | 'services_en' | 'services_ar' | 'feature_en' | 'feature_ar',
    index: number
  ) => {
    setFormData((prev) => {
      const array = [...(prev[arrayKey] as any[])];
      array[index] = { ...array[index], icon: null };
      return { ...prev, [arrayKey]: array };
    });
  };


  const createMutation = useMutation({
    mutationFn: (data: any) => servicesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      toast.success(t('serviceCreated'));
      navigate('/services');
    },
    onError: () => {
      toast.error(t('errorOccurred'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => servicesApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      toast.success(t('serviceUpdated'));
      navigate('/services');
    },
    onError: (error: any) => {
      console.error('Update error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || t('errorOccurred');
      toast.error(errorMessage);
    },
  });

  
  const handleSubmit = (e: React.FormEvent) => {
  e.preventDefault();

  const preparedData = prepareFormData(formData);
  
  // Debug: Log FormData contents
  console.log('Submitting form data:', {
    isEdit,
    id,
    formDataKeys: Object.keys(formData),
  });
  
  // Log FormData entries for debugging
  if (preparedData instanceof FormData) {
    console.log('=== FormData entries (in handleSubmit) ===');
    const entries: { [key: string]: any } = {};
    const criticalFields = ['feature_title_en', 'feature_title_ar', 'feature_icon'];
    
    Array.from(preparedData.entries()).forEach(([key, value]) => {
      entries[key] = value;
      // Special logging for feature fields
      if (key.includes('feature_title') || key === 'feature_icon') {
        console.log(`[FEATURE FIELD] ${key}:`, `"${value}"`, `(type: ${typeof value}, is empty: ${value === ''}, length: ${String(value).length})`);
      } else {
        console.log(`${key}:`, value);
      }
    });
    
    // Verify critical fields are present - check ALL important fields, not just features
    console.log('=== Critical Fields Verification ===');
    const allCriticalFields = [
      'feature_title_en', 'feature_title_ar', 'feature_icon',
      'services_title_en', 'services_title_ar', 'services_icon',
      'ai_title_en', 'ai_title_ar', 'ai_icon',
      'transformation_title_en', 'transformation_title_ar', 'transformation_icon',
      'branches_title_en', 'branches_title_ar', 'branches_icon'
    ];
    allCriticalFields.forEach(field => {
      const found = Array.from(preparedData.entries()).filter(([key]) => key === field);
      if (found.length > 0) {
        const value = found[0][1];
        const isEmpty = value === '';
        console.log(`✓ ${field} found in FormData:`, `"${value}"`, `(isEmpty: ${isEmpty})`);
      } else {
        console.error(`✗ ${field} MISSING from FormData!`);
      }
    });
    
    console.log('Feature arrays:', {
      feature_en: formData.feature_en,
      feature_ar: formData.feature_ar,
      feature_en_length: formData.feature_en.length,
      feature_ar_length: formData.feature_ar.length
    });
    console.log('Feature titles from formData state:', {
      feature_title_en: formData.feature_title_en,
      feature_title_ar: formData.feature_title_ar,
      feature_icon: formData.feature_icon
    });
  }

  if (isEdit) {
    updateMutation.mutate(preparedData);
  } else {
    createMutation.mutate(preparedData);
  }
};



  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'order' ? parseInt(value) || 0 : value,
    }));
  };

  const addProduct = (lang: 'en' | 'ar') => {
    const newProduct = lang === 'en' ? newProductEn : newProductAr;
    if (newProduct.name.trim()) {
      setFormData(prev => ({
        ...prev,
        [`products_${lang}`]: [
          ...prev[`products_${lang}` as keyof typeof prev] as Product[],
          { name: newProduct.name, description: newProduct.description || '' }
        ],
      }));
      if (lang === 'en') {
        setNewProductEn({ name: '', description: '' });
      } else {
        setNewProductAr({ name: '', description: '' });
      }
    }
  };

  const removeProduct = (lang: 'en' | 'ar', index: number) => {
    setFormData(prev => ({
      ...prev,
      [`products_${lang}`]: (prev[`products_${lang}` as keyof typeof prev] as Product[]).filter((_, i) => i !== index),
    }));
  };

  if (isLoading && (isEdit || isView)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">{t('loading')}</div>
      </div>
    );
  }


  
  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4 rtl:space-x-reverse">
        <Button variant="outline" onClick={() => navigate('/services')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {i18n.language === 'ar' ? 'العودة' : 'Back'}
        </Button>
        <h1 className="text-3xl font-bold">
          {isView
            ? (i18n.language === 'ar' ? 'عرض الخدمة' : 'View Service')
            : isEdit 
            ? (i18n.language === 'ar' ? 'تعديل الخدمة' : 'Edit Service')
            : (i18n.language === 'ar' ? 'إضافة خدمة جديدة' : 'Add New Service')
          }
        </h1>
      </div>

      <form onSubmit={handleSubmit} className={`space-y-6 ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.language === 'ar' ? 'المعلومات الأساسية' : 'Basic Information'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="title_en">{i18n.language === 'ar' ? 'العنوان (إنجليزي)' : 'Title (English)'}</Label>
                <Input
                  id="title_en"
                  name="title_en"
                  value={formData.title_en}
                  onChange={handleChange}
                  required
                  dir="ltr"
                  disabled={isView}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title_ar">{i18n.language === 'ar' ? 'العنوان (عربي)' : 'Title (Arabic)'}</Label>
                <Input
                  id="title_ar"
                  name="title_ar"
                  value={formData.title_ar}
                  onChange={handleChange}
                  required
                  dir="rtl"
                  disabled={isView}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div className="space-y-2">
                <Label htmlFor="subtitle_en">{i18n.language === 'ar' ? 'العنوان الفرعي (إنجليزي)' : 'Subtitle (English)'}</Label>
                <Input
                  id="subtitle_en"
                  name="subtitle_en"
                  value={formData.subtitle_en}
                  onChange={handleChange}
                  dir="ltr"
                  disabled={isView}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subtitle_ar">{i18n.language === 'ar' ? 'العنوان الفرعي (عربي)' : 'Subtitle (Arabic)'}</Label>
                <Input
                  id="subtitle_ar"
                  name="subtitle_ar"
                  value={formData.subtitle_ar}
                  onChange={handleChange}
                  dir="rtl"
                  disabled={isView}
                />
              </div>
            </div>
{/* 
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div className="space-y-2">
                <Label htmlFor="icon_en">{i18n.language === 'ar' ? 'الأيقونة (إنجليزي)' : 'Icon (English)'}</Label>
                <Input
                  id="icon_en"
                  name="icon_en"
                  value={formData.icon_en}
                  onChange={handleChange}
                  placeholder="🔧"
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="icon_ar">{i18n.language === 'ar' ? 'الأيقونة (عربي)' : 'Icon (Arabic)'}</Label>
                <Input
                  id="icon_ar"
                  name="icon_ar"
                  value={formData.icon_ar}
                  onChange={handleChange}
                  placeholder="🔧"
                  dir="rtl"
                />
              </div>
            </div>

            <div className="mt-4">
              <div className="space-y-2">
                <Label htmlFor="order">{i18n.language === 'ar' ? 'ترتيب العرض' : 'Display Order'}</Label>
                <Input
                  id="order"
                  name="order"
                  type="number"
                  value={formData.order}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div> */}
          </CardContent>
        </Card>

        {/* Description */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.language === 'ar' ? 'الوصف' : 'Description'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="description_part1_en">{i18n.language === 'ar' ? 'الوصف الجزء الأول (إنجليزي)' : 'Description Part 1 (English)'}</Label>
                <Textarea
                  id="description_part1_en"
                  name="description_part1_en"
                  value={formData.description_part1_en}
                  onChange={handleChange}
                  rows={4}
                  dir="ltr"
                  disabled={isView}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description_part1_ar">{i18n.language === 'ar' ? 'الوصف الجزء الأول (عربي)' : 'Description Part 1 (Arabic)'}</Label>
                <Textarea
                  id="description_part1_ar"
                  name="description_part1_ar"
                  value={formData.description_part1_ar}
                  onChange={handleChange}
                  rows={4}
                  dir="rtl"
                  disabled={isView}
                />
              </div>
            </div>

            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div className="space-y-2">
                <Label htmlFor="description_part2_en">{i18n.language === 'ar' ? 'الوصف الجزء الثاني (إنجليزي)' : 'Description Part 2 (English)'}</Label>
                <Textarea
                  id="description_part2_en"
                  name="description_part2_en"
                  value={formData.description_part2_en}
                  onChange={handleChange}
                  rows={4}
                  dir="ltr"
                  disabled={isView}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description_part2_ar">{i18n.language === 'ar' ? 'الوصف الجزء الثاني (عربي)' : 'Description Part 2 (Arabic)'}</Label>
                <Textarea
                  id="description_part2_ar"
                  name="description_part2_ar"
                  value={formData.description_part2_ar}
                  onChange={handleChange}
                  rows={4}
                  dir="rtl"
                  disabled={isView}
                />
              </div>
            </div> */}
          </CardContent>
        </Card>

        {/* Products Section */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.language === 'ar' ? 'المنتجات/المميزات' : 'Products/Features'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="products_title_en">{i18n.language === 'ar' ? 'عنوان المنتجات (إنجليزي)' : 'Products Title (English)'}</Label>
                <Input
                  id="products_title_en"
                  name="products_title_en"
                  value={formData.products_title_en}
                  onChange={handleChange}
                  dir="ltr"
                  disabled={isView}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="products_title_ar">{i18n.language === 'ar' ? 'عنوان المنتجات (عربي)' : 'Products Title (Arabic)'}</Label>
                <Input
                  id="products_title_ar"
                  name="products_title_ar"
                  value={formData.products_title_ar}
                  onChange={handleChange}
                  dir="rtl"
                  disabled={isView}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {/* English Products */}
              <div className="space-y-4">
                <Label>{i18n.language === 'ar' ? 'المنتجات/المميزات (إنجليزي)' : 'Products/Features (English)'}</Label>
                <div className="space-y-2">
                  {formData.products_en.map((product, index) => (
                    <div key={index} className="flex items-center space-x-2 rtl:space-x-reverse p-2 bg-gray-100 rounded">
                      <div className="flex-1" dir="ltr">
                        <div className="font-medium text-sm">{product.name}</div>
                        {product.description && (
                          <div className="text-xs text-gray-600 mt-1">{product.description}</div>
                        )}
                      </div>
                      {!isView && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => removeProduct('en', index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {!isView && (
                    <div className="space-y-2">
                      <Input
                        value={newProductEn.name}
                        onChange={(e) => setNewProductEn({ ...newProductEn, name: e.target.value })}
                        placeholder={i18n.language === 'ar' ? 'اسم المنتج/الميزة...' : 'Product/Feature name...'}
                        dir="ltr"
                      />
                      <Input
                        value={newProductEn.description}
                        onChange={(e) => setNewProductEn({ ...newProductEn, description: e.target.value })}
                        placeholder={i18n.language === 'ar' ? 'وصف المنتج/الميزة...' : 'Product/Feature description...'}
                        dir="ltr"
                      />
                      <Button
                        type="button"
                        onClick={() => addProduct('en')}
                        disabled={!newProductEn.name.trim()}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Arabic Products */}
              <div className="space-y-4">
                <Label>{i18n.language === 'ar' ? 'المنتجات/المميزات (عربي)' : 'Products/Features (Arabic)'}</Label>
                <div className="space-y-2">
                  {formData.products_ar.map((product, index) => (
                    <div key={index} className="flex items-center space-x-2 rtl:space-x-reverse p-2 bg-gray-100 rounded">
                      <div className="flex-1" dir="rtl">
                        <div className="font-medium text-sm">{product.name}</div>
                        {product.description && (
                          <div className="text-xs text-gray-600 mt-1">{product.description}</div>
                        )}
                      </div>
                      {!isView && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => removeProduct('ar', index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {!isView && (
                    <div className="space-y-2">
                      <Input
                        value={newProductAr.name}
                        onChange={(e) => setNewProductAr({ ...newProductAr, name: e.target.value })}
                        placeholder={i18n.language === 'ar' ? 'اسم المنتج/الميزة...' : 'Product/Feature name...'}
                        dir="rtl"
                      />
                      <Input
                        value={newProductAr.description}
                        onChange={(e) => setNewProductAr({ ...newProductAr, description: e.target.value })}
                        placeholder={i18n.language === 'ar' ? 'وصف المنتج/الميزة...' : 'Product/Feature description...'}
                        dir="rtl"
                      />
                      <Button
                        type="button"
                        onClick={() => addProduct('ar')}
                        disabled={!newProductAr.name.trim()}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>{/* Digital Transformation Section */}
<Card>
  <CardHeader>
    <CardTitle>{i18n.language === 'ar' ? 'التحول الرقمي' : 'Digital Transformation'}</CardTitle>
  </CardHeader>

  <CardContent>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

      {/* Titles */}
      <div className="space-y-2">
        <Label>{i18n.language === 'ar' ? 'عنوان (إنجليزي)' : 'Title (English)'}</Label>
        <Input
          name="transformation_title_en"
          value={formData.transformation_title_en}
          onChange={handleChange}
          dir="ltr"
          disabled={isView}
        />
      </div>

      <div className="space-y-2">
        <Label>{i18n.language === 'ar' ? 'عنوان (عربي)' : 'Title (Arabic)'}</Label>
        <Input
          name="transformation_title_ar"
          value={formData.transformation_title_ar}
          onChange={handleChange}
          dir="rtl"
          disabled={isView}
        />
      </div>
    </div>

    {/* Icon Upload */}
    <div className="mt-4">
      <Label>{i18n.language === 'ar' ? 'أيقونة القسم' : 'Section Icon'}</Label>
      <div className="mt-2">
        <Input
          type="file"
          accept="image/*"
          onChange={(e) => handleIconUpload(e, 'transformation_icon')}
          disabled={isView}
          className="cursor-pointer"
        />
        {formData.transformation_icon && typeof formData.transformation_icon === 'string' && (
          <div className="mt-2 flex items-center gap-2">
            <img
              src={formData.transformation_icon}
              alt="Icon preview"
              className="h-16 w-16 object-contain border rounded"
            />
            {!isView && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, transformation_icon: null }))}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>


    {/*  */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">

      {/* EN  */}
      <div className="space-y-4">
        <Label>{i18n.language === 'ar' ? 'البرامج (إنجليزي)' : 'Programs (English)'}</Label>
      {formData.transformation_steps_en.map((item, index) => (
  <div key={index} className="flex flex-col gap-2 p-2 border rounded">
    <div className="flex items-center gap-2">
      {item.icon && typeof item.icon === 'string' && (
        <div className="flex-shrink-0">
          <img src={item.icon} alt="Icon" className="h-12 w-12 object-contain border rounded" />
        </div>
      )}
      <div className="flex-1">
        <div className="font-medium text-sm p-2 bg-gray-100 rounded" dir="ltr">{item.step}</div>
        {item.description && (
          <div className="text-xs text-gray-600 mt-1 p-2 bg-gray-50 rounded">{item.description}</div>
        )}
        {!isView && (
          <div className="mt-2">
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => handleItemIconUpload(e, 'transformation_steps_en', index)}
              className="text-xs"
              size={1}
            />
            {item.icon && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleItemIconRemove('transformation_steps_en', index)}
                className="mt-1"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
      </div>
      {!isView && (
        <Button variant="destructive" size="sm" onClick={() => removeStep('en', index)}>
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  </div>
))}

{/* Add new step */}
{!isView && (
  <div className="space-y-2">
    <Input
      value={newStepEn.step}
      onChange={(e) => setNewStepEn({ ...newStepEn, step: e.target.value })}
      placeholder="Step name..."
      dir="ltr"
    />
    <Input
      value={newStepEn.description}
      onChange={(e) => setNewStepEn({ ...newStepEn, description: e.target.value })}
      placeholder="Step description..."
      dir="ltr"
    />
    <Input
      type="file"
      accept="image/*"
      onChange={async (e) => {
        const file = e.target.files?.[0];
        if (file) {
          try {
            toast.loading(i18n.language === 'ar' ? 'جاري رفع الأيقونة...' : 'Uploading icon...');
            const response = await uploadApi.uploadImage(file);
            if (response.data.success) {
              setNewStepEn({ ...newStepEn, icon: response.data.url });
              toast.dismiss();
              toast.success(i18n.language === 'ar' ? 'تم رفع الأيقونة بنجاح' : 'Icon uploaded successfully');
            }
          } catch (error: any) {
            toast.dismiss();
            toast.error(error?.response?.data?.message || (i18n.language === 'ar' ? 'فشل رفع الأيقونة' : 'Failed to upload icon'));
          }
        }
      }}
      className="text-xs"
    />
    {newStepEn.icon && (
      <div className="flex items-center gap-2">
        <img src={newStepEn.icon} alt="Preview" className="h-12 w-12 object-contain border rounded" />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setNewStepEn({ ...newStepEn, icon: null })}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    )}
    <Button onClick={() => addStep('en')} disabled={!newStepEn.step.trim()}>
      <Plus className="h-4 w-4" />
    </Button>
  </div>
)}

      </div>

      {/* AR  */}
      <div className="space-y-4">
        <Label>{i18n.language === 'ar' ? 'البرامج (عربي)' : 'Programs (Arabic)'}</Label>
    {formData.transformation_steps_ar.map((item, index) => (
  <div key={index} className="flex flex-col gap-2 p-2 border rounded">
    <div className="flex items-center gap-2 rtl:space-x-reverse">
      {item.icon && typeof item.icon === 'string' && (
        <div className="flex-shrink-0">
          <img src={item.icon} alt="Icon" className="h-12 w-12 object-contain border rounded" />
        </div>
      )}
      <div className="flex-1">
        <div className="font-medium text-sm p-2 bg-gray-100 rounded" dir="rtl">{item.step}</div>
        {item.description && (
          <div className="text-xs text-gray-600 mt-1 p-2 bg-gray-50 rounded">{item.description}</div>
        )}
        {!isView && (
          <div className="mt-2">
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => handleItemIconUpload(e, 'transformation_steps_ar', index)}
              className="text-xs"
              size={1}
            />
            {item.icon && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleItemIconRemove('transformation_steps_ar', index)}
                className="mt-1"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
      </div>
      {!isView && (
        <Button variant="destructive" size="sm" onClick={() => removeStep('ar', index)}>
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  </div>
))}

{/* Add new step */}
{!isView && (
  <div className="space-y-2">
    <Input
      dir="rtl"
      value={newStepAr.step}
      onChange={(e) => setNewStepAr({ ...newStepAr, step: e.target.value })}
      placeholder="اسم الخطوة..."
    />
    <Input
      dir="rtl"
      value={newStepAr.description}
      onChange={(e) => setNewStepAr({ ...newStepAr, description: e.target.value })}
      placeholder="وصف الخطوة..."
    />
    <Input
      type="file"
      accept="image/*"
      onChange={async (e) => {
        const file = e.target.files?.[0];
        if (file) {
          try {
            toast.loading(i18n.language === 'ar' ? 'جاري رفع الأيقونة...' : 'Uploading icon...');
            const response = await uploadApi.uploadImage(file);
            if (response.data.success) {
              setNewStepAr({ ...newStepAr, icon: response.data.url });
              toast.dismiss();
              toast.success(i18n.language === 'ar' ? 'تم رفع الأيقونة بنجاح' : 'Icon uploaded successfully');
            }
          } catch (error: any) {
            toast.dismiss();
            toast.error(error?.response?.data?.message || (i18n.language === 'ar' ? 'فشل رفع الأيقونة' : 'Failed to upload icon'));
          }
        }
      }}
      className="text-xs"
    />
    {newStepAr.icon && (
      <div className="flex items-center gap-2">
        <img src={newStepAr.icon} alt="Preview" className="h-12 w-12 object-contain border rounded" />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setNewStepAr({ ...newStepAr, icon: null })}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    )}
    <Button onClick={() => addStep('ar')} disabled={!newStepAr.step.trim()}>
      <Plus className="h-4 w-4" />
    </Button>
  </div>
)}

      </div>
    </div>
  </CardContent>
</Card>

<Card>
  <CardHeader>
    <CardTitle>{i18n.language === 'ar' ? 'خدمات متينة ومرنة' : 'Solid & Flexible Services'}</CardTitle>
  </CardHeader>

  <CardContent>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Title EN */}
      <div className="space-y-2">
        <Label>{i18n.language === 'ar' ? 'عنوان (إنجليزي)' : 'Title (English)'}</Label>
        <Input
          name="services_title_en"
          value={formData.services_title_en}
          onChange={handleChange}
          dir="ltr"
          disabled={isView}
        />
      </div>

      {/* Title AR */}
      <div className="space-y-2">
        <Label>{i18n.language === 'ar' ? 'عنوان (عربي)' : 'Title (Arabic)'}</Label>
        <Input
          name="services_title_ar"
          value={formData.services_title_ar}
          onChange={handleChange}
          dir="rtl"
          disabled={isView}
        />
      </div>
    </div>

    {/* Icon Upload */}
    <div className="mt-4">
      <Label>{i18n.language === 'ar' ? 'أيقونة القسم' : 'Section Icon'}</Label>
      <div className="mt-2">
        <Input
          type="file"
          accept="image/*"
          onChange={(e) => handleIconUpload(e, 'services_icon')}
          disabled={isView}
          className="cursor-pointer"
        />
        {formData.services_icon && typeof formData.services_icon === 'string' && (
          <div className="mt-2 flex items-center gap-2">
            <img
              src={formData.services_icon}
              alt="Icon preview"
              className="h-16 w-16 object-contain border rounded"
            />
            {!isView && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, services_icon: null }))}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>

    {/* Lists */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">

      {/* English Services */}
      <div className="space-y-4">
        <Label>{i18n.language === 'ar' ? 'الخدمات (إنجليزي)' : 'Services (English)'}</Label>

        {formData.services_en.map((item, index) => (
          <div key={index} className="flex flex-col gap-2 p-2 border rounded">
            <div className="flex items-center gap-2">
              {item.icon && typeof item.icon === 'string' && (
                <div className="flex-shrink-0">
                  <img src={item.icon} alt="Icon" className="h-12 w-12 object-contain border rounded" />
                </div>
              )}
              <div className="flex-1">
                <div className="font-medium text-sm p-2 bg-gray-100 rounded" dir="ltr">{item.name}</div>
                {item.description && (
                  <div className="text-xs text-gray-600 mt-1 p-2 bg-gray-50 rounded">{item.description}</div>
                )}
                {!isView && (
                  <div className="mt-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleItemIconUpload(e, 'services_en', index)}
                      className="text-xs"
                      size={1}
                    />
                    {item.icon && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleItemIconRemove('services_en', index)}
                        className="mt-1"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {!isView && (
                <Button variant="destructive" size="sm" onClick={() => removeItem('en', index)}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}

        {!isView && (
          <div className="space-y-2">
            <Input
              value={newServiceEn.name}
              onChange={(e) => setNewServiceEn({ ...newServiceEn, name: e.target.value })}
              placeholder="Service name..."
              dir="ltr"
            />
            <Input
              value={newServiceEn.description}
              onChange={(e) => setNewServiceEn({ ...newServiceEn, description: e.target.value })}
              placeholder="Service description..."
              dir="ltr"
            />
            <Input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  try {
                    toast.loading(i18n.language === 'ar' ? 'جاري رفع الأيقونة...' : 'Uploading icon...');
                    const response = await uploadApi.uploadImage(file);
                    if (response.data.success) {
                      setNewServiceEn({ ...newServiceEn, icon: response.data.url });
                      toast.dismiss();
                      toast.success(i18n.language === 'ar' ? 'تم رفع الأيقونة بنجاح' : 'Icon uploaded successfully');
                    }
                  } catch (error: any) {
                    toast.dismiss();
                    toast.error(error?.response?.data?.message || (i18n.language === 'ar' ? 'فشل رفع الأيقونة' : 'Failed to upload icon'));
                  }
                }
              }}
              className="text-xs"
            />
            {newServiceEn.icon && (
              <div className="flex items-center gap-2">
                <img src={newServiceEn.icon} alt="Preview" className="h-12 w-12 object-contain border rounded" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setNewServiceEn({ ...newServiceEn, icon: null })}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
            <Button onClick={() => addItem('en')} disabled={!newServiceEn.name.trim()}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Arabic Services */}
      <div className="space-y-4">
        <Label>{i18n.language === 'ar' ? 'الخدمات (عربي)' : 'Services (Arabic)'}</Label>

        {formData.services_ar.map((item, index) => (
          <div key={index} className="flex flex-col gap-2 p-2 border rounded rtl:space-x-reverse">
            <div className="flex items-center gap-2 rtl:space-x-reverse">
              {item.icon && typeof item.icon === 'string' && (
                <div className="flex-shrink-0">
                  <img src={item.icon} alt="Icon" className="h-12 w-12 object-contain border rounded" />
                </div>
              )}
              <div className="flex-1">
                <div className="font-medium text-sm p-2 bg-gray-100 rounded" dir="rtl">{item.name}</div>
                {item.description && (
                  <div className="text-xs text-gray-600 mt-1 p-2 bg-gray-50 rounded">{item.description}</div>
                )}
                {!isView && (
                  <div className="mt-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleItemIconUpload(e, 'services_ar', index)}
                      className="text-xs"
                      size={1}
                    />
                    {item.icon && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleItemIconRemove('services_ar', index)}
                        className="mt-1"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {!isView && (
                <Button variant="destructive" size="sm" onClick={() => removeItem('ar', index)}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}

        {!isView && (
          <div className="space-y-2">
            <Input
              value={newServiceAr.name}
              onChange={(e) => setNewServiceAr({ ...newServiceAr, name: e.target.value })}
              placeholder="اسم الخدمة..."
              dir="rtl"
            />
            <Input
              value={newServiceAr.description}
              onChange={(e) => setNewServiceAr({ ...newServiceAr, description: e.target.value })}
              placeholder="وصف الخدمة..."
              dir="rtl"
            />
            <Input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  try {
                    toast.loading(i18n.language === 'ar' ? 'جاري رفع الأيقونة...' : 'Uploading icon...');
                    const response = await uploadApi.uploadImage(file);
                    if (response.data.success) {
                      setNewServiceAr({ ...newServiceAr, icon: response.data.url });
                      toast.dismiss();
                      toast.success(i18n.language === 'ar' ? 'تم رفع الأيقونة بنجاح' : 'Icon uploaded successfully');
                    }
                  } catch (error: any) {
                    toast.dismiss();
                    toast.error(error?.response?.data?.message || (i18n.language === 'ar' ? 'فشل رفع الأيقونة' : 'Failed to upload icon'));
                  }
                }
              }}
              className="text-xs"
            />
            {newServiceAr.icon && (
              <div className="flex items-center gap-2">
                <img src={newServiceAr.icon} alt="Preview" className="h-12 w-12 object-contain border rounded" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setNewServiceAr({ ...newServiceAr, icon: null })}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
            <Button onClick={() => addItem('ar')} disabled={!newServiceAr.name.trim()}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  </CardContent>
</Card>
<Card>
  <CardHeader>
    <CardTitle>{i18n.language === 'ar' ? 'الفروع' : 'Branches'}</CardTitle>
  </CardHeader>

  <CardContent className="space-y-6">
{/* Titles */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

      <div className="space-y-2">
        <Label>{i18n.language === 'ar' ? 'العنوان (إنجليزي)' : 'Title (English)'}</Label>
        <Input
          name="branches_title_en"
          value={formData.branches_title_en}
          onChange={handleChange}
          disabled={isView}
          dir="ltr"
        />

      </div>

      <div className="space-y-2">
        <Label>{i18n.language === 'ar' ? 'العنوان (عربي)' : 'Title (Arabic)'}</Label>
        <Input
          name="branches_title_ar"
          value={formData.branches_title_ar}
          onChange={handleChange}
          disabled={isView}
          dir="rtl"
        />
      </div>
    </div>
    {/* ================== Branches EN ================== */}
    <div className="space-y-4">
      <Label>{i18n.language === 'ar' ? 'الفروع (إنجليزي)' : 'Branches (English)'}</Label>
      {formData.branches_en.map((item, index) => (
        <div key={index} className="flex flex-col gap-2 p-2 border rounded">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <div className="font-medium text-sm p-2 bg-gray-100 rounded" dir="ltr">{item.name}</div>
              {item.description && (
                <div className="text-xs text-gray-600 mt-1 p-2 bg-gray-50 rounded">{item.description}</div>
              )}
            </div>
            {!isView && (
              <Button variant="destructive" size="sm" onClick={() => removeBranch('en', index)}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ))}

      {!isView && (
        <div className="space-y-2">
          <Input
            value={newBranchEn.name}
            onChange={(e) => setNewBranchEn({ ...newBranchEn, name: e.target.value })}
            placeholder="Branch name..."
            dir="ltr"
          />
          <Input
            value={newBranchEn.description}
            onChange={(e) => setNewBranchEn({ ...newBranchEn, description: e.target.value })}
            placeholder="Branch description..."
            dir="ltr"
          />
          <Button onClick={() => addBranch('en')} disabled={!newBranchEn.name.trim()}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>

    {/* ================== Branches AR ================== */}
    <div className="space-y-4">
      <Label>{i18n.language === 'ar' ? 'الفروع (عربي)' : 'Branches (Arabic)'}</Label>
      {formData.branches_ar.map((item, index) => (
        <div key={index} className="flex flex-col gap-2 p-2 border rounded">
          <div className="flex items-center gap-2 rtl:space-x-reverse">
            <div className="flex-1">
              <div className="font-medium text-sm p-2 bg-gray-100 rounded" dir="rtl">{item.name}</div>
              {item.description && (
                <div className="text-xs text-gray-600 mt-1 p-2 bg-gray-50 rounded">{item.description}</div>
              )}
            </div>
            {!isView && (
              <Button variant="destructive" size="sm" onClick={() => removeBranch('ar', index)}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ))}

      {!isView && (
        <div className="space-y-2">
          <Input
            dir="rtl"
            value={newBranchAr.name}
            onChange={(e) => setNewBranchAr({ ...newBranchAr, name: e.target.value })}
            placeholder="اسم الفرع..."
          />
          <Input
            dir="rtl"
            value={newBranchAr.description}
            onChange={(e) => setNewBranchAr({ ...newBranchAr, description: e.target.value })}
            placeholder="وصف الفرع..."
          />
          <Button onClick={() => addBranch('ar')} disabled={!newBranchAr.name.trim()}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>

  </CardContent>
</Card>

{/* AI Solutions Section */}
<Card className="mt-6">

  <CardHeader>
    <CardTitle>{i18n.language === 'ar' ? 'حلول الذكاء الاصطناعي' : 'AI Solutions'}</CardTitle>
  </CardHeader>

  <CardContent>
    {/* Titles */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-2">
        <Label>{i18n.language === 'ar' ? 'العنوان (إنجليزي)' : 'Title (English)'}</Label>
        <Input
          name="ai_title_en"
          value={formData.ai_title_en}
          onChange={handleChange}
          dir="ltr"
          disabled={isView}
        />
      </div>

      <div className="space-y-2">
        <Label>{i18n.language === 'ar' ? 'العنوان (عربي)' : 'Title (Arabic)'}</Label>
        <Input
          name="ai_title_ar"
          value={formData.ai_title_ar}
          onChange={handleChange}
          dir="rtl"
          disabled={isView}
        />
      </div>
    </div>



    {/* AI Cards */}
    {/* AI Cards */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
  <div className="space-y-4">
    <Label>مزايا الذكاء الاصطناعي (انجليزي)</Label>

    {/* Inputs لإدخال الميزة الجديدة */}
    {!isView && (
      <div className="space-y-2 p-3 border rounded-lg bg-gray-50">
        <Input
          placeholder="New feature title..."
          value={newAiEn.title}
          onChange={(e) => setNewAiEn({ ...newAiEn, title: e.target.value })}
        />
        <textarea
          placeholder="New feature description..."
          value={newAiEn.description}
          onChange={(e) => setNewAiEn({ ...newAiEn, description: e.target.value })}
          className="w-full p-2 border rounded"
          rows={2}
        />
      </div>
    )}

    {/* عرض الميزات الموجودة */}
    {formData.ai_cards_en?.map((item, index) => (
      <div key={`ai-en-${index}`} className="space-y-2 p-3 border rounded-lg bg-gray-50">
        <Input
          placeholder="Feature title..."
          value={item.title}
          onChange={(e) => handleAiChange('en', index, 'title', e.target.value)}
          disabled={isView}
        />
        <textarea
          placeholder="Feature description..."
          value={item.description}
          onChange={(e) => handleAiChange('en', index, 'description', e.target.value)}
          className="w-full p-2 border rounded"
          disabled={isView}
          rows={2}
        />
        {!isView && (
          <Button variant="destructive" size="sm" onClick={() => removeAi('en', index)}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    ))}

    {/* زر الإضافة */}
    {!isView && (
      <Button size="sm" onClick={() => addAi('en')} disabled={!newAiEn.title.trim() && !newAiEn.description.trim()}>
        Add Feature <Plus className="h-4 w-4 ml-2" />
      </Button>
    )}
  </div>

  {/* نفس الشيء للعربية */}
  <div className="space-y-4">
    <Label>مزايا الذكاء الاصطناعي (عربي)</Label>

    {!isView && (
      <div className="space-y-2 p-3 border rounded-lg bg-gray-50" dir="rtl">
        <Input
          placeholder="عنوان الميزة الجديدة..."
          value={newAiAr.title}
          onChange={(e) => setNewAiAr({ ...newAiAr, title: e.target.value })}
        />
        <textarea
          placeholder="وصف الميزة الجديدة..."
          value={newAiAr.description}
          onChange={(e) => setNewAiAr({ ...newAiAr, description: e.target.value })}
          className="w-full p-2 border rounded"
          rows={2}
        />
      </div>
    )}

    {formData.ai_cards_ar?.map((item, index) => (
      <div key={`ai-ar-${index}`} className="space-y-2 p-3 border rounded-lg bg-gray-50" dir="rtl">
        <Input
          placeholder="عنوان الميزة..."
          value={item.title}
          onChange={(e) => handleAiChange('ar', index, 'title', e.target.value)}
          disabled={isView}
        />
        <textarea
          placeholder="وصف الميزة..."
          value={item.description}
          onChange={(e) => handleAiChange('ar', index, 'description', e.target.value)}
          className="w-full p-2 border rounded"
          rows={2}
          disabled={isView}
        />
        {!isView && (
          <Button variant="destructive" size="sm" onClick={() => removeAi('ar', index)}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    ))}

    {!isView && (
      <Button size="sm" onClick={() => addAi('ar')} disabled={!newAiAr.title.trim() && !newAiAr.description.trim()}>
        إضافة ميزة <Plus className="h-4 w-4 mr-2" />
      </Button>
    )}
  </div>
</div>
  </CardContent>
</Card>

  {/* Products Section */}
   <Card>
  <CardHeader>
    <CardTitle>{i18n.language === 'ar' ? 'البنية التحتية السحابية' : 'Cloud Infrastructure'}</CardTitle>
  </CardHeader>

  <CardContent>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Feature Title Inputs */}
      <div className="space-y-2">
        <Label>{i18n.language === 'ar' ? 'عنوان الميزة (إنجليزي)' : 'Feature Title (English)'}</Label>
        <Input
          name="feature_title_en"
          value={formData.feature_title_en}
          onChange={handleChange}
          dir="ltr"
          disabled={isView}
        />
      </div>

      <div className="space-y-2">
        <Label>{i18n.language === 'ar' ? 'عنوان الميزة (عربي)' : 'Feature Title (Arabic)'}</Label>
        <Input
          name="feature_title_ar"
          value={formData.feature_title_ar}
          onChange={handleChange}
          dir="rtl"
          disabled={isView}
        />
      </div>
    </div>

    {/* Icon Upload */}
    <div className="mt-4">
      <Label>{i18n.language === 'ar' ? 'أيقونة القسم' : 'Section Icon'}</Label>
      <div className="mt-2">
        <Input
          type="file"
          accept="image/*"
          onChange={(e) => handleIconUpload(e, 'feature_icon')}
          disabled={isView}
          className="cursor-pointer"
        />
        {formData.feature_icon && typeof formData.feature_icon === 'string' && (
          <div className="mt-2 flex items-center gap-2">
            <img
              src={formData.feature_icon}
              alt="Icon preview"
              className="h-16 w-16 object-contain border rounded"
            />
            {!isView && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, feature_icon: null }))}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>

    {/* Features List */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
      {/* EN Features */}
      <div className="space-y-4">
        <Label>{i18n.language === 'ar' ? 'الميزات (إنجليزي)' : 'Features (English)'}</Label>
        {formData.feature_en.map((item, index) => (
          <div key={index} className="flex flex-col gap-2 p-2 border rounded">
            <div className="flex items-center gap-2">
              {item.icon && typeof item.icon === 'string' && (
                <div className="flex-shrink-0">
                  <img src={item.icon} alt="Icon" className="h-12 w-12 object-contain border rounded" />
                </div>
              )}
              <div className="flex-1">
                <div className="font-medium text-sm p-2 bg-gray-100 rounded" dir="ltr">{item.title}</div>
                {item.description && (
                  <div className="text-xs text-gray-600 mt-1 p-2 bg-gray-50 rounded">{item.description}</div>
                )}
                {!isView && (
                  <div className="mt-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleItemIconUpload(e, 'feature_en', index)}
                      className="text-xs"
                      size={1}
                    />
                    {item.icon && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleItemIconRemove('feature_en', index)}
                        className="mt-1"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {!isView && (
                <Button variant="destructive" size="sm" onClick={() => removeFeature('en', index)}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}

        {/* Add New Feature */}
        {!isView && (
          <div className="space-y-2">
            <Input
              value={newFeatureEn.title}
              onChange={(e) => setNewFeatureEn({ ...newFeatureEn, title: e.target.value })}
              placeholder="Feature title..."
              dir="ltr"
            />
            <Input
              value={newFeatureEn.description}
              onChange={(e) => setNewFeatureEn({ ...newFeatureEn, description: e.target.value })}
              placeholder="Feature description..."
              dir="ltr"
            />
            <Input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  try {
                    toast.loading(i18n.language === 'ar' ? 'جاري رفع الأيقونة...' : 'Uploading icon...');
                    const response = await uploadApi.uploadImage(file);
                    if (response.data.success) {
                      setNewFeatureEn({ ...newFeatureEn, icon: response.data.url });
                      toast.dismiss();
                      toast.success(i18n.language === 'ar' ? 'تم رفع الأيقونة بنجاح' : 'Icon uploaded successfully');
                    }
                  } catch (error: any) {
                    toast.dismiss();
                    toast.error(error?.response?.data?.message || (i18n.language === 'ar' ? 'فشل رفع الأيقونة' : 'Failed to upload icon'));
                  }
                }
              }}
              className="text-xs"
            />
            {newFeatureEn.icon && (
              <div className="flex items-center gap-2">
                <img src={newFeatureEn.icon} alt="Preview" className="h-12 w-12 object-contain border rounded" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setNewFeatureEn({ ...newFeatureEn, icon: null })}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
            <Button onClick={() => addFeature('en')} disabled={!newFeatureEn.title.trim()}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* AR Features */}
      <div className="space-y-4">
        <Label>{i18n.language === 'ar' ? 'الميزات (عربي)' : 'Features (Arabic)'}</Label>
        {formData.feature_ar.map((item, index) => (
          <div key={index} className="flex flex-col gap-2 p-2 border rounded">
            <div className="flex items-center gap-2 rtl:space-x-reverse">
              {item.icon && typeof item.icon === 'string' && (
                <div className="flex-shrink-0">
                  <img src={item.icon} alt="Icon" className="h-12 w-12 object-contain border rounded" />
                </div>
              )}
              <div className="flex-1">
                <div className="font-medium text-sm p-2 bg-gray-100 rounded" dir="rtl">{item.title}</div>
                {item.description && (
                  <div className="text-xs text-gray-600 mt-1 p-2 bg-gray-50 rounded">{item.description}</div>
                )}
                {!isView && (
                  <div className="mt-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleItemIconUpload(e, 'feature_ar', index)}
                      className="text-xs"
                      size={1}
                    />
                    {item.icon && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleItemIconRemove('feature_ar', index)}
                        className="mt-1"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {!isView && (
                <Button variant="destructive" size="sm" onClick={() => removeFeature('ar', index)}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}

        {/* Add New Feature */}
        {!isView && (
          <div className="space-y-2">
            <Input
              value={newFeatureAr.title}
              onChange={(e) => setNewFeatureAr({ ...newFeatureAr, title: e.target.value })}
              placeholder="عنوان الميزة..."
              dir="rtl"
            />
            <Input
              value={newFeatureAr.description}
              onChange={(e) => setNewFeatureAr({ ...newFeatureAr, description: e.target.value })}
              placeholder="وصف الميزة..."
              dir="rtl"
            />
            <Input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  try {
                    toast.loading(i18n.language === 'ar' ? 'جاري رفع الأيقونة...' : 'Uploading icon...');
                    const response = await uploadApi.uploadImage(file);
                    if (response.data.success) {
                      setNewFeatureAr({ ...newFeatureAr, icon: response.data.url });
                      toast.dismiss();
                      toast.success(i18n.language === 'ar' ? 'تم رفع الأيقونة بنجاح' : 'Icon uploaded successfully');
                    }
                  } catch (error: any) {
                    toast.dismiss();
                    toast.error(error?.response?.data?.message || (i18n.language === 'ar' ? 'فشل رفع الأيقونة' : 'Failed to upload icon'));
                  }
                }
              }}
              className="text-xs"
            />
            {newFeatureAr.icon && (
              <div className="flex items-center gap-2">
                <img src={newFeatureAr.icon} alt="Preview" className="h-12 w-12 object-contain border rounded" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setNewFeatureAr({ ...newFeatureAr, icon: null })}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
            <Button onClick={() => addFeature('ar')} disabled={!newFeatureAr.title.trim()}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  </CardContent>
</Card>
        {/* Submit Button or Edit Button */}
        <div className="flex justify-end">
          {isView ? (
            <Button 
              type="button" 
              onClick={() => navigate(`/services/${id}/edit`)}
            >
              <Edit className="h-4 w-4 mr-2" />
              {i18n.language === 'ar' ? 'تعديل الخدمة' : 'Edit Service'}
            </Button>
          ) : (
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              <Save className="h-4 w-4 mr-2" />
              {createMutation.isPending || updateMutation.isPending
                ? (i18n.language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
                : (isEdit 
                  ? (i18n.language === 'ar' ? 'تحديث الخدمة' : 'Update Service')
                  : (i18n.language === 'ar' ? 'إنشاء الخدمة' : 'Create Service')
                )
              }
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ServiceForm;